/**
 * Simple to Builder JSON Transformer
 *
 * Converts the simplified AI-friendly JSON format into the complex Builder JSON
 * format required by the React presentation builder.
 *
 * The transformation handles:
 * 1. Plain strings → Slate.js text format
 * 2. Flat items → Nested block/smart_block/content_block structure
 * 3. Simple arrays → items with IDs
 * 4. Page layout → pageLayout/cardBackground data
 */

import type {
  SimpleAlignment,
  SimpleBlockQuoteBlock,
  SimpleCommonBlockStyle,
  SimpleChartBlock,
  SimpleDocument,
  SimpleFillableChildItem,
  SimpleFillableFieldNode,
  SimpleFillableTextBlock,
  SimpleFillableTextContent,
  SimpleFillableTextNode,
  SimpleItem,
  SimplePage,
  SimplePageHeaderFooterElement,
  SimplePageHeaderFooterPreserve,
  SimplePricingColumnRole,
  SimplePricingTableBlock,
  SimpleRichContent,
  SimpleSmartBlockItem,
  SimpleSmartTextContent,
  SimpleSmartTextItem,
  SimpleTextChildItem,
  SimpleTableCellContent,
  SimpleTableCellFormat,
  SimpleTableEmbeddedBlock,
  SimpleTableCellValue,
  SimpleTextBlock,
  SimpleTextGroupBlock,
  SimpleTextContent,
  SimpleTextNode,
  SimpleCodeBlock,
} from './simple-builder-schema';
import { inferTableColumnWidths } from './table-column-widths';

import {
  getContentBlockType,
  isSimpleArrowBlock,
  isSimpleBlockQuoteBlock,
  isSimpleCodeBlock,
  isSimpleButtonBlock,
  isSimpleCalloutBlock,
  isSimpleChartBlock,
  isSimpleCircularArrowBlock,
  isSimpleContentBlock,
  isSimpleDividerBlock,
  isSimpleEditorSignatureBlock,
  isSimpleEditorInitialsBlock,
  isSimpleEditorStampBlock,
  isSimpleEditorFillableTextBlock,
  isSimpleFillableTextBlock,
  isSimpleFunnelBlock,
  isSimpleIconBlock,
  isSimpleIconTextBlock,
  isSimpleImageBlock,
  isSimpleLabelBlock,
  isSimplePageBreakBlock,
  isSimplePricingTableBlock,
  isSimpleProcessFlowBlock,
  isSimplePyramidBlock,
  isSimpleQuoteBoxesBlock,
  isSimpleQuotationBuilderBlock,
  isSimpleRecipientInitialsBlock,
  isSimpleRecipientSignatureBlock,
  isSimpleRecipientStampBlock,
  isSimpleRowBlock,
  isSimpleSideLineBoxesBlock,
  isSimpleSolidBoxesBlock,
  isSimpleSpaceBlock,
  isSimpleStaircaseBlock,
  isSimpleStepsBlock,
  isSimpleTableBlock,
  isSimpleTextBlock,
  isSimpleTextGroupBlock,
  isSimpleTimelineBlock,
  isSimpleTocBlock,
  isSimpleTopCircleBoxesBlock,
  isSimpleVideoBlock,
  isSimpleEmbedWebpageBlock,
} from './simple-builder-schema';

import type {
  SimpleEditorSignatureBlock,
  SimpleRecipientInitialsBlock,
  SimpleRecipientSignatureBlock,
  SimpleRecipientStampBlock,
  SimpleEditorInitialsBlock,
  SimpleEditorStampBlock,
  SimpleEditorFillableTextBlock,
  SimpleListItem,
} from './simple-builder-schema';

import { normalizeLiteJsonDocument } from './lite-json-parser';
import { resolveThemeColorExpression } from './theme-color-tokens';
import { SMART_BLOCK_DEFAULTS } from './transformer-constants';

// ============================================================================
// SLATE.JS TEXT CONVERSION
// ============================================================================

/** Slate.js text node */
interface SlateText {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  code?: boolean;
  color?: string;
  fontSize?: string | number;
}

/** Slate.js element node */
interface SlateElement {
  type: string;
  children: (SlateText | SlateElement)[];
  url?: string;
  newTab?: boolean;
  align?: SimpleAlignment;
  listStyle?: string;
  textStyle?: string;
  indentLevel?: number;
  restartNumbering?: boolean;
  listVariant?: string;
  character?: string;
  variable?: {
    key: string;
    value?: string;
    type?: 'system' | 'custom';
    isOverride?: boolean;
  };
  fieldType?: string;
  fieldId?: string;
  placeholder?: string;
  value?: string;
  required?: boolean;
  options?: Array<{ label: string; value: string }>;
  multiSelect?: boolean;
  min?: string | number;
  max?: string | number;
  rows?: number;
  maxLength?: number;
  data?: Record<string, unknown>;
}

/**
 * Known system variable keys
 */
const SYSTEM_VARIABLE_KEYS = [
  'project.title',
  'project.createdDate',
  'project.sentDate',
  'project.expirationDate',
  'project.referenceNumber',
  'project.value',
  'sender.email',
  'sender.company',
  'sender.address',
  'sender.domain',
];

/**
 * Determine if a variable key is a system variable or custom variable
 */
function getVariableType(key: string): 'system' | 'custom' {
  // If it starts with 'custom.' it's definitely custom
  if (key.startsWith('custom.')) {
    return 'custom';
  }
  // Check if it's a known system variable
  if (SYSTEM_VARIABLE_KEYS.includes(key)) {
    return 'system';
  }
  // Default to custom for unknown variables
  return 'custom';
}

/**
 * Mention element for variables
 */
interface MentionElement {
  type: 'mention';
  character: string;
  children: SlateText[];
  variable: {
    key: string;
    value: string;
    type: 'system' | 'custom';
    isOverride?: boolean;
  };
}

/**
 * Check if text is purely a variable reference (e.g., "[[variable.key]]")
 * Returns the variable key if it is, null otherwise
 */
function extractVariableKey(text: string): string | null {
  const variableMatch = text.match(/^\[\[([^\]]+)\]\]$/);
  return variableMatch?.[1] ?? null;
}

/**
 * Create a mention element for a variable
 */
function buildInlineTextMarks(node: {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  code?: boolean;
  color?: string;
  fontSize?: string | number;
}) {
  return {
    ...(node.bold ? { bold: true } : {}),
    ...(node.italic ? { italic: true } : {}),
    ...(node.underline ? { underline: true } : {}),
    ...(node.code ? { code: true } : {}),
    ...(node.color ? { color: node.color } : {}),
    ...(node.fontSize !== undefined ? { fontSize: node.fontSize } : {}),
  };
}

function createMentionElement(
  variableKey: string,
  marks: {
    override?: string;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    code?: boolean;
    color?: string;
    fontSize?: string | number;
  } = {},
): MentionElement {
  const varType = getVariableType(variableKey);
  const isOverride = marks.override !== undefined;
  return {
    type: 'mention',
    character: marks.override ?? '',
    children: [{ text: '', ...buildInlineTextMarks(marks) }],
    variable: {
      key: variableKey,
      value: marks.override ?? '',
      type: varType,
      isOverride,
    },
  };
}

/**
 * Parse text with markdown-style formatting (bold with **text**) and variable mentions ([[variable]])
 * Handles nested cases like **[[variable]]** (bold variable)
 * Returns an array of Slate text nodes and mention elements with appropriate formatting
 */
function parseTextWithFormatting(text: string): (SlateText | MentionElement)[] {
  const nodes: (SlateText | MentionElement)[] = [];

  // Combined regex to match both **bold text** and [[variable]] patterns
  // Order matters: we process them in order of appearance
  const combinedRegex = /(\*\*(.+?)\*\*)|(\[\[([^\]]+)\]\])/g;

  let lastIndex = 0;
  let match;

  while ((match = combinedRegex.exec(text)) !== null) {
    // Add text before the match (if any)
    if (match.index > lastIndex) {
      const beforeText = text.slice(lastIndex, match.index);
      if (beforeText) {
        nodes.push({ text: beforeText });
      }
    }

    if (match[1]) {
      // Bold text match: **text**
      const boldContent = match[2] ?? '';

      // Check if the bold content is a variable (e.g., **[[variable]]** or **'[[variable]]'**)
      // Handle cases like **[[var]]** or **'[[var]]'**
      const cleanedContent = boldContent.replace(/^['"]|['"]$/g, ''); // Remove surrounding quotes if any
      const variableKey = extractVariableKey(cleanedContent);

      if (variableKey) {
        // This is a bold variable - create a bold mention element
        nodes.push(createMentionElement(variableKey, { bold: true }));
      } else {
        // Check if bold content contains variables mixed with text
        // e.g., **Hello [[name]]!** - need to parse the inner content
        const innerVariableRegex = /\[\[([^\]]+)\]\]/g;
        if (innerVariableRegex.test(boldContent)) {
          // Parse the inner content for variables, making everything bold
          const innerNodes = parseInnerBoldContent(boldContent);
          nodes.push(...innerNodes);
        } else {
          // Regular bold text without variables
          nodes.push({ text: boldContent, bold: true });
        }
      }
    } else if (match[3]) {
      // Variable match: [[variable.key]]
      const variableKey = match[4] ?? '';
      // Create mention element with proper type
      nodes.push(createMentionElement(variableKey));
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last match
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    if (remainingText) {
      nodes.push({ text: remainingText });
    }
  }

  // If no matches were found, return the original text
  if (nodes.length === 0) {
    nodes.push({ text });
  }

  return nodes;
}

/**
 * Parse content inside bold markers that may contain variables mixed with text
 * e.g., "Hello [[name]]!" -> [bold text "Hello "], [bold mention "name"], [bold text "!"]
 */
function parseInnerBoldContent(content: string): (SlateText | MentionElement)[] {
  const nodes: (SlateText | MentionElement)[] = [];
  const variableRegex = /\[\[([^\]]+)\]\]/g;

  let lastIndex = 0;
  let match;

  while ((match = variableRegex.exec(content)) !== null) {
    // Add bold text before the variable
    if (match.index > lastIndex) {
      const beforeText = content.slice(lastIndex, match.index);
      if (beforeText) {
        nodes.push({ text: beforeText, bold: true });
      }
    }

    // Add bold mention
    const variableKey = match[1] ?? '';
    nodes.push(createMentionElement(variableKey, { bold: true }));

    lastIndex = match.index + match[0].length;
  }

  // Add remaining bold text
  if (lastIndex < content.length) {
    const remainingText = content.slice(lastIndex);
    if (remainingText) {
      nodes.push({ text: remainingText, bold: true });
    }
  }

  return nodes;
}

function normalizeCommonBlockStyle(
  style?: SimpleCommonBlockStyle,
  alignment?: SimpleAlignment,
): Record<string, unknown> | undefined {
  const { align, ...restStyle } = style || {};
  const normalized = {
    ...restStyle,
    ...(align || alignment ? { justifyContent: align || alignment } : {}),
  };

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

function formatFontSizeWithUnit(fontSize?: string | number): string | undefined {
  if (fontSize === undefined || fontSize === null) return undefined;
  if (typeof fontSize === 'number') {
    return isNaN(fontSize) ? undefined : `${fontSize}px`;
  }
  const str = String(fontSize).trim();
  if (!str) return undefined;
  if (
    str.endsWith('px') ||
    str.endsWith('rem') ||
    str.endsWith('em') ||
    str.endsWith('pt') ||
    str.endsWith('%') ||
    str.endsWith('vw') ||
    str.endsWith('vh')
  ) {
    return str;
  }
  const num = Number(str);
  if (!isNaN(num)) {
    return `${num}px`;
  }
  return str;
}

function normalizeTextBlockStyle(
  style?: SimpleTextBlock['style'],
): Record<string, unknown> | undefined {
  if (!style) return undefined;

  const { align, fontSize, textStyle: _textStyle, ...restStyle } = style;
  const formattedFontSize = formatFontSizeWithUnit(fontSize);

  const normalized = Object.fromEntries(
    Object.entries({
      ...restStyle,
      ...(formattedFontSize !== undefined ? { fontSize: formattedFontSize } : {}),
      ...(align ? { justifyContent: align } : {}),
    }).filter(([, value]) => value !== undefined),
  );

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

function richNodePath(basePath: string, childIndex: number): string {
  return `${basePath}[${childIndex}]`;
}

function richNodeToSlate(node: any, path: string): SlateText | SlateElement {
  if (!node || typeof node !== 'object') {
    throw new Error(
      `Invalid rich node at ${path}. Expected an object with either { text }, { var }, or { type, children }.
Received: ${JSON.stringify(node)}`,
    );
  }

  if ('text' in node) {
    return {
      text: node.text,
      ...(node.bold ? { bold: true } : {}),
      ...(node.italic ? { italic: true } : {}),
      ...(node.underline ? { underline: true } : {}),
      ...(node.code ? { code: true } : {}),
      ...(node.color ? { color: node.color } : {}),
      ...(node.fontSize !== undefined ? { fontSize: node.fontSize } : {}),
    };
  }

  if ('var' in node) {
    return createMentionElement(node.var, node);
  }

  if (!('type' in node)) {
    throw new Error(
      `Invalid rich node at ${path}. Missing "type" for element node. Expected { type, children } or a leaf node { text } / { var }.
Received keys: ${Object.keys(node).join(', ') || '(none)'}`,
    );
  }

  if (!Array.isArray(node.children)) {
    throw new Error(
      `Invalid rich node at ${path}. "children" must be an array for element nodes (type: ${String(node.type)}).
Received: ${JSON.stringify(node.children)}`,
    );
  }

  return {
    type: node.type,
    children: node.children.map((child: unknown, childIndex: number) =>
      richNodeToSlate(child, richNodePath(`${path}.children`, childIndex)),
    ),
    ...(node.url ? { url: node.url } : {}),
    ...(node.newTab ? { newTab: true } : {}),
    ...(node.align ? { align: node.align } : {}),
    ...(node.listStyle ? { listStyle: node.listStyle } : {}),
    ...(node.textStyle ? { textStyle: node.textStyle } : {}),
    ...(node.indentLevel ? { indentLevel: node.indentLevel } : {}),
    ...(node.restartNumbering ? { restartNumbering: true } : {}),
    ...(node.listVariant ? { listVariant: node.listVariant } : {}),
    ...(node.character ? { character: node.character } : {}),
    ...(node.variable ? { variable: node.variable } : {}),
    ...(node.fieldType ? { fieldType: node.fieldType } : {}),
    ...(node.fieldId ? { fieldId: node.fieldId } : {}),
    ...(node.placeholder ? { placeholder: node.placeholder } : {}),
    ...(node.value !== undefined ? { value: node.value } : {}),
    ...(typeof node.required === 'boolean' ? { required: node.required } : {}),
    ...(Array.isArray(node.options) ? { options: node.options } : {}),
    ...(node.data ? { data: node.data } : {}),
  };
}

// Top-level leaves and variables next to each other are one line of text ("Meet {company}'s
// team"), so they share a paragraph instead of becoming one paragraph each.
function richContentToSlate(content: SimpleRichContent, path: string): SlateElement[] {
  const blocks: SlateElement[] = [];
  let line: SlateElement | null = null;
  content.forEach((node, nodeIndex) => {
    const slateNode = richNodeToSlate(node, richNodePath(path, nodeIndex));
    const isInline = !('type' in slateNode) || slateNode.type === 'mention';
    if (!isInline) {
      line = null;
      blocks.push(slateNode);
      return;
    }
    if (!line) {
      line = { type: 'paragraph', children: [] };
      blocks.push(line);
    }
    line.children.push(slateNode);
  });
  return blocks;
}

function inlineTextNodeToSlateChildren(
  node: SimpleTextNode,
): Array<SlateText | SlateElement | MentionElement> {
  if ('text' in node) {
    return [
      {
        text: node.text,
        ...buildInlineTextMarks(node),
      },
    ];
  }

  if ('var' in node) {
    return [createMentionElement(node.var, node)];
  }

  return [
    {
      type: 'link',
      url: node.link,
      ...(node.newTab ? { newTab: true } : {}),
      children: inlineTextContentToSlateChildren(node.content),
    },
  ];
}

function inlineTextContentToSlateChildren(
  content: SimpleTextContent,
): Array<SlateText | SlateElement | MentionElement> {
  return content.flatMap((node) => inlineTextNodeToSlateChildren(node));
}

function inlineTextContentToSlateElement(
  content: SimpleTextContent,
  type: string = 'paragraph',
): SlateElement[] {
  const children = inlineTextContentToSlateChildren(content);

  return [
    {
      type,
      children: children.length > 0 ? children : [{ text: '' }],
    },
  ];
}

function isSimpleFillableFieldNode(value: unknown): value is SimpleFillableFieldNode {
  return !!value && typeof value === 'object' && 'field' in value;
}

function buildDefaultFieldPlaceholder(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
}

function buildInlineFieldElement(node: SimpleFillableFieldNode): SlateElement {
  const field = node.field;
  const fieldType = field.type || 'text_input';

  return {
    type: 'inline_field',
    fieldType,
    fieldId: generateUniqueFieldId(),
    placeholder: field.placeholder || buildDefaultFieldPlaceholder(fieldType),
    value: '',
    required: typeof field.required === 'boolean' ? field.required : fieldType !== 'checkbox',
    ...(Array.isArray(field.options) ? { options: field.options } : {}),
    ...(typeof field.multiSelect === 'boolean' ? { multiSelect: field.multiSelect } : {}),
    ...(field.min !== undefined ? { min: field.min } : {}),
    ...(field.max !== undefined ? { max: field.max } : {}),
    ...(typeof field.rows === 'number' ? { rows: field.rows } : {}),
    ...(typeof field.maxLength === 'number' ? { maxLength: field.maxLength } : {}),
    children: [{ text: '' }],
  };
}

function fillableTextNodeToSlateChildren(
  node: SimpleFillableTextNode,
): Array<SlateText | SlateElement | MentionElement> {
  if (isSimpleFillableFieldNode(node)) {
    return [buildInlineFieldElement(node)];
  }

  return inlineTextNodeToSlateChildren(node as SimpleTextNode);
}

function fillableTextContentToSlateChildren(
  content: SimpleFillableTextContent,
): Array<SlateText | SlateElement | MentionElement> {
  return content.flatMap((node) => fillableTextNodeToSlateChildren(node));
}

function fillableTextContentToSlateElement(
  content: SimpleFillableTextContent,
  type: string = 'paragraph',
): SlateElement[] {
  const children = fillableTextContentToSlateChildren(content);

  return [
    {
      type,
      children: children.length > 0 ? children : [{ text: '' }],
    },
  ];
}

function mergeTextBlockStyles(
  inheritedStyle?: SimpleTextBlock['style'],
  itemStyle?: SimpleTextBlock['style'],
): SimpleTextBlock['style'] | undefined {
  const merged = Object.fromEntries(
    Object.entries({
      ...(inheritedStyle || {}),
      ...(itemStyle || {}),
    }).filter(([, value]) => value !== undefined),
  );

  return Object.keys(merged).length > 0 ? (merged as SimpleTextBlock['style']) : undefined;
}

function applySlateNodeBlockStyle(
  node: SlateElement,
  style?: SimpleTextBlock['style'],
): SlateElement {
  if (!style) return node;

  return {
    ...node,
    ...(style.align ? { align: style.align } : {}),
    ...(style.textStyle ? { textStyle: style.textStyle } : {}),
    ...(style.listStyle && (node.type === 'bulleted-list' || node.type === 'numbered-list')
      ? { listStyle: style.listStyle }
      : {}),
  };
}

function isInlineTextNode(value: unknown): value is SimpleTextNode {
  if (!value || typeof value !== 'object') return false;
  return 'text' in value || 'var' in value || 'link' in value;
}

function isSimpleSmartTextItem(value: unknown): value is SimpleSmartTextItem {
  return (
    !!value &&
    typeof value === 'object' &&
    ('block_quote' in value || isSimpleTextBlock(value as any))
  );
}

function applySmartTextStyle(node: SlateElement, style?: SimpleTextBlock['style']): SlateElement {
  if (!style) return node;

  const nextNode: SlateElement = {
    ...node,
    ...(style.align ? { align: style.align } : {}),
    ...(style.textStyle ? { textStyle: style.textStyle } : {}),
    ...(style.listStyle && (node.type === 'bulleted-list' || node.type === 'numbered-list')
      ? { listStyle: style.listStyle }
      : {}),
  };

  const formattedFontSize = formatFontSizeWithUnit(style.fontSize);

  if (!formattedFontSize && !style.color) {
    return nextNode;
  }

  const applyMarks = (
    children: Array<SlateText | SlateElement | MentionElement>,
  ): Array<SlateText | SlateElement | MentionElement> =>
    children.map((child) => {
      if ('text' in child) {
        return {
          ...child,
          ...(formattedFontSize && child.fontSize === undefined
            ? { fontSize: formattedFontSize }
            : {}),
          ...(style.color && child.color === undefined ? { color: style.color } : {}),
        };
      }

      return {
        ...child,
        children: applyMarks(child.children as Array<SlateText | SlateElement | MentionElement>),
      };
    });

  return {
    ...nextNode,
    children: applyMarks(nextNode.children as Array<SlateText | SlateElement | MentionElement>),
  };
}

function smartTextItemToSlateContent(item: SimpleSmartTextItem): SlateElement[] {
  if ('block_quote' in item) {
    if (Array.isArray(item.block_quote)) {
      return [
        {
          type: 'block-quote',
          children: inlineTextContentToSlateChildren(item.block_quote),
        },
      ];
    }

    return [
      applySmartTextStyle(
        {
          type: 'block-quote',
          children: inlineTextContentToSlateChildren(item.block_quote.content),
        },
        item.block_quote.style,
      ),
    ];
  }

  const heading = getHeading(item);

  if (heading) {
    return inlineTextContentToSlateElement(heading.content, heading.type).map((node) =>
      applySmartTextStyle(node, item.style),
    );
  }

  if (item.p) {
    return inlineTextContentToSlateElement(item.p).map((node) =>
      applySmartTextStyle(node, item.style),
    );
  }

  if (item.bullets) {
    return structuredListToSlate(item.bullets, 'bulleted-list', item.style?.listStyle).map((node) =>
      applySmartTextStyle(node, item.style),
    );
  }

  if (item.numbered) {
    return structuredListToSlate(item.numbered, 'numbered-list', item.style?.listStyle).map(
      (node) => applySmartTextStyle(node, item.style),
    );
  }

  if (item.todo) {
    return structuredListToSlate(item.todo, 'todo-list', item.style?.listStyle).map((node) =>
      applySmartTextStyle(node, item.style),
    );
  }

  return [{ type: 'paragraph', children: [{ text: '' }] }];
}

function textChildItemToSlateContent(
  item: SimpleTextChildItem,
  inheritedStyle?: SimpleTextBlock['style'],
): SlateElement[] {
  const itemStyle = 'style' in item ? item.style : undefined;
  const mergedStyle = mergeTextBlockStyles(inheritedStyle, itemStyle);
  const itemLeafStyle = mergedStyle
    ? {
        fontSize: mergedStyle.fontSize,
        color: mergedStyle.color,
      }
    : undefined;

  if ('block_quote' in item) {
    if (Array.isArray(item.block_quote)) {
      return [
        applySlateNodeBlockStyle(
          {
            type: 'block-quote',
            children: inlineTextContentToSlateChildren(item.block_quote),
          },
          mergedStyle,
        ),
      ];
    }

    return [
      applySmartTextStyle(
        applySlateNodeBlockStyle(
          {
            type: 'block-quote',
            children: inlineTextContentToSlateChildren(item.block_quote.content),
          },
          mergeTextBlockStyles(inheritedStyle, item.block_quote.style),
        ),
        itemLeafStyle,
      ),
    ];
  }

  const heading = getHeading(item);

  if (heading) {
    return inlineTextContentToSlateElement(heading.content, heading.type).map((node) =>
      applySmartTextStyle(applySlateNodeBlockStyle(node, mergedStyle), itemLeafStyle),
    );
  }

  if (item.p) {
    return inlineTextContentToSlateElement(item.p).map((node) =>
      applySmartTextStyle(applySlateNodeBlockStyle(node, mergedStyle), itemLeafStyle),
    );
  }

  if (item.bullets) {
    return structuredListToSlate(item.bullets, 'bulleted-list', mergedStyle?.listStyle).map(
      (node) => applySmartTextStyle(applySlateNodeBlockStyle(node, mergedStyle), itemLeafStyle),
    );
  }

  if (item.numbered) {
    return structuredListToSlate(item.numbered, 'numbered-list', mergedStyle?.listStyle).map(
      (node) => applySmartTextStyle(applySlateNodeBlockStyle(node, mergedStyle), itemLeafStyle),
    );
  }

  if (item.todo) {
    return structuredListToSlate(item.todo, 'todo-list', mergedStyle?.listStyle).map((node) =>
      applySmartTextStyle(applySlateNodeBlockStyle(node, mergedStyle), itemLeafStyle),
    );
  }

  return [{ type: 'paragraph', children: [{ text: '' }] }];
}

function smartTextContentToSlateValue(content: SimpleSmartTextContent): SlateElement[] {
  if (
    Array.isArray(content) &&
    (content.length === 0 || content.every((node) => isInlineTextNode(node)))
  ) {
    return inlineTextContentToSlateElement(content as SimpleTextContent);
  }

  if (Array.isArray(content) && content.every((item) => isSimpleSmartTextItem(item))) {
    const nodes = (content as SimpleSmartTextItem[]).flatMap((item) =>
      smartTextItemToSlateContent(item),
    );
    return nodes.length > 0 ? nodes : [{ type: 'paragraph', children: [{ text: '' }] }];
  }

  return [{ type: 'paragraph', children: [{ text: '' }] }];
}

/**
 * Convert plain text to Slate.js paragraph format
 * Supports markdown-style bold formatting (**text**)
 */
const HEADING_ELEMENT_TYPES = [
  ['h1', 'heading-one'],
  ['h2', 'heading-two'],
  ['h3', 'heading-three'],
  ['h4', 'heading-four'],
  ['h5', 'heading-five'],
] as const;

function getHeading<Content>(
  item: Partial<Record<(typeof HEADING_ELEMENT_TYPES)[number][0], Content>>,
) {
  for (const [key, type] of HEADING_ELEMENT_TYPES) {
    const content = item[key];
    if (content) return { content, type };
  }
  return undefined;
}

function textToSlate(text: string): SlateElement[] {
  // Split by newlines to create multiple paragraphs
  const paragraphs = text.split('\n').filter((p) => p.trim());

  if (paragraphs.length === 0) {
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }

  return paragraphs.map((p) => ({
    type: 'paragraph',
    children: parseTextWithFormatting(p),
  }));
}

/**
 * Convert plain text to Slate.js heading format
 * Supports markdown-style bold formatting (**text**)
 * @param text - The heading text
 * @param level - Heading level (one, two, three)
 * @param fontSize - Optional font size override (e.g., "72px")
 */
function headingToSlate(
  text: string,
  level: 'one' | 'two' | 'three',
  fontSize?: string,
): SlateElement[] {
  const children = parseTextWithFormatting(text);

  // If fontSize is specified, add it to each text node
  if (fontSize) {
    children.forEach((child: any) => {
      child.fontSize = fontSize;
    });
  }

  return [
    {
      type: `heading-${level}`,
      children,
    },
  ];
}

/**
 * Convert bullets array to Slate.js bulleted list
 * Supports markdown-style bold formatting (**text**)
 */
function bulletsToSlate(items: string[]): SlateElement[] {
  return [
    {
      type: 'bulleted-list',
      children: items.map((item) => ({
        type: 'list-item',
        children: parseTextWithFormatting(item),
      })),
    },
  ];
}

/**
 * Convert numbered array to Slate.js numbered list
 * Supports markdown-style bold formatting (**text**)
 */
function numberedToSlate(items: string[]): SlateElement[] {
  return [
    {
      type: 'numbered-list',
      children: items.map((item) => ({
        type: 'list-item',
        children: parseTextWithFormatting(item),
      })),
    },
  ];
}

function blockQuoteToSlate(text: string): SlateElement[] {
  return text
    .split('\n')
    .filter((paragraph) => paragraph.trim())
    .map((paragraph) => ({
      type: 'block-quote',
      children: parseTextWithFormatting(paragraph),
    }));
}

function toSlateListItem<Content extends unknown[]>(
  item: SimpleListItem<Content>,
  toChildren: (content: Content) => Array<SlateText | SlateElement | MentionElement>,
): SlateElement {
  const detail: {
    content: Content;
    indent?: number;
    restart?: boolean;
    align?: SimpleAlignment;
    variant?: string;
  } = Array.isArray(item) ? { content: item } : item;

  return {
    type: 'list-item',
    ...(detail.align ? { align: detail.align } : {}),
    ...(detail.indent ? { indentLevel: detail.indent } : {}),
    ...(detail.restart ? { restartNumbering: true } : {}),
    ...(detail.variant ? { listVariant: detail.variant } : {}),
    children: toChildren(detail.content),
  };
}

function structuredListToSlate(
  items: SimpleListItem[],
  type: 'bulleted-list' | 'numbered-list' | 'todo-list',
  listStyle?: string,
): SlateElement[] {
  return [
    {
      type,
      ...(listStyle ? { listStyle } : {}),
      children: items.map((item) => toSlateListItem(item, inlineTextContentToSlateChildren)),
    },
  ];
}

function fillableStructuredListToSlate(
  items: SimpleListItem<SimpleFillableTextContent>[],
  type: 'bulleted-list' | 'numbered-list',
  listStyle?: string,
): SlateElement[] {
  return [
    {
      type,
      ...(listStyle ? { listStyle } : {}),
      children: items.map((item) => toSlateListItem(item, fillableTextContentToSlateChildren)),
    },
  ];
}

function alignmentToJustifyContent(alignment?: SimpleAlignment): string | undefined {
  return alignment;
}

function buildAlignmentStyle(alignment?: SimpleAlignment): Record<string, unknown> | undefined {
  const justifyContent = alignmentToJustifyContent(alignment);
  return justifyContent ? { justifyContent } : undefined;
}

function buildTextAlignStyle(alignment?: SimpleAlignment): Record<string, unknown> | undefined {
  return alignment ? { textAlign: alignment } : undefined;
}

function generateLocalId(prefix: string, index: number): string {
  return `${prefix}_${Date.now().toString(36)}_${index}_${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeSmartBlockValue<T extends Record<string, any>>(
  value: SimpleSmartBlockItem[] | T,
): { items: SimpleSmartBlockItem[]; config: Omit<T, 'items'> } {
  if (Array.isArray(value)) {
    return { items: value, config: {} as Omit<T, 'items'> };
  }

  const { items, ...config } = value;
  return {
    items: Array.isArray(items) ? items : [],
    config,
  };
}

function createBlock(type: string, data: Record<string, any>): any {
  return {
    type: 'block',
    block: {
      type,
      data,
    },
  };
}

function createSmartBlock(type: string, data: Record<string, any>): any {
  return {
    type: 'smart_block',
    block: {
      type,
      data,
    },
  };
}

// ============================================================================
// BLOCK TRANSFORMERS
// ============================================================================

/**
 * Transform simple text block to Builder text block
 */
function transformTextBlock(item: SimpleTextBlock): any {
  let content: SlateElement[] = [];
  const blockType = 'text';

  const heading = getHeading(item);

  if (heading) {
    content = inlineTextContentToSlateElement(heading.content, heading.type).map((node) =>
      applySmartTextStyle(node, item.style),
    );
  } else if (item.p) {
    content = inlineTextContentToSlateElement(item.p).map((node) =>
      applySmartTextStyle(node, item.style),
    );
  } else if (item.bullets) {
    content = structuredListToSlate(item.bullets, 'bulleted-list', item.style?.listStyle).map(
      (node) => applySmartTextStyle(node, item.style),
    );
  } else if (item.numbered) {
    content = structuredListToSlate(item.numbered, 'numbered-list', item.style?.listStyle).map(
      (node) => applySmartTextStyle(node, item.style),
    );
  } else if (item.todo) {
    content = structuredListToSlate(item.todo, 'todo-list', item.style?.listStyle).map((node) =>
      applySmartTextStyle(node, item.style),
    );
  }

  const data: any = { content };

  const style = normalizeTextBlockStyle(item.style);
  if (style) {
    data.style = style;
  }

  return createBlock(blockType, data);
}

function transformTextGroupBlock(item: SimpleTextGroupBlock): any {
  const content = item.text.flatMap((child) => textChildItemToSlateContent(child, item.style));
  const data: any = {
    content: content.length > 0 ? content : [{ type: 'paragraph', children: [{ text: '' }] }],
  };

  const style = normalizeTextBlockStyle(item.style);
  if (style) {
    data.style = style;
  }

  return createBlock('text', data);
}

// Resolves image refs (e.g. "img_1") to URLs during a conversion. Set per-call by the
// entry points below; conversion is synchronous so there's no cross-call interleaving.
let imageRefUrlMap: Record<string, string> | null = null;

/**
 * Transform simple image block to Builder image block
 */
function transformImageBlock(item: { image: string | Record<string, any> }): any {
  const imageData = typeof item.image === 'string' ? { url: item.image } : item.image;
  const style = normalizeCommonBlockStyle(imageData.style, imageData.align);
  const resolvedUrl =
    imageData.url ?? (imageData.ref ? imageRefUrlMap?.[imageData.ref] : undefined);
  const fitToWidth = typeof imageData.fitToWidth === 'boolean' ? imageData.fitToWidth : undefined;

  return createBlock('image', {
    ...(imageData.alt ? { alt: imageData.alt } : {}),
    ...(imageData.prompt ? { image_prompt: imageData.prompt } : {}),
    ...(imageData.image_search_query ? { image_search_query: imageData.image_search_query } : {}),
    // A server-delivered URL wins over a prompt: applyServerImages clears the
    // prompt when a URL lands (FEAT-042), so this branch is inert on success.
    // With only a prompt present, the block keeps it for the client effect.
    ...(resolvedUrl && !imageData.prompt ? { image: resolvedUrl } : {}),
    ...(typeof imageData.height === 'number' ? { height: imageData.height } : {}),
    ...(imageData.width !== undefined ? { width: imageData.width } : {}),
    ...(imageData.maskShape ? { maskShape: imageData.maskShape } : {}),
    ...(typeof fitToWidth === 'boolean' ? { fitToWidth } : {}),
    ...(imageData.cropData ? { cropData: imageData.cropData } : {}),
    ...(imageData.focusPoint ? { focusPoint: imageData.focusPoint } : {}),
    ...(imageData.caption ? { caption: imageData.caption } : {}),
    ...(imageData.colorCode ? { colorCode: imageData.colorCode } : {}),
    ...(imageData.image_generation_request_id
      ? { image_generation_request_id: imageData.image_generation_request_id }
      : {}),
    // Server-side generation failure state (FEAT-042); the effects treat
    // `image_generation_status: 'failed'` as do-not-generate.
    ...(imageData.image_generation_error
      ? { image_generation_error: imageData.image_generation_error }
      : {}),
    ...(imageData.image_generation_status
      ? { image_generation_status: imageData.image_generation_status }
      : {}),
    ...(style ? { style } : {}),
  });
}

/**
 * Transform simple video block to Builder video block
 */
function transformVideoBlock(item: { video: Record<string, any> }): any {
  const videoData = item.video || {};
  const src = videoData.video || videoData.src || '';
  const style = normalizeCommonBlockStyle(videoData.style, videoData.align);

  return createBlock('video', {
    video: src,
    src,
    platform: videoData.platform || (src ? 'video' : undefined),
    title: videoData.title || '',
    description: videoData.description || '',
    displayMode: videoData.displayMode || 'player',
    previewThumbnail: videoData.previewThumbnail || '',
    previewTitle: videoData.previewTitle || '',
    previewDescription: videoData.previewDescription || '',
    previewWebsite: videoData.previewWebsite || '',
    ...(typeof videoData.height === 'number' ? { height: videoData.height } : {}),
    ...(videoData.width !== undefined ? { width: videoData.width } : {}),
    ...(typeof videoData.aspectRatio === 'number' ? { aspectRatio: videoData.aspectRatio } : {}),
    ...(style ? { style } : {}),
  });
}

/**
 * Transform simple embed_webpage block to Builder embed_webpage block
 */
function transformEmbedWebpageBlock(item: { embed_webpage: Record<string, any> }): any {
  const embedData = item.embed_webpage || {};
  const src = embedData.video || embedData.src || '';
  const style = normalizeCommonBlockStyle(embedData.style, embedData.align);

  return createBlock('embed_webpage', {
    video: src,
    src,
    platform: embedData.platform || 'embedded',
    title: embedData.title || '',
    description: embedData.description || '',
    displayMode: embedData.displayMode || 'player',
    previewThumbnail: embedData.previewThumbnail || '',
    previewTitle: embedData.previewTitle || '',
    previewDescription: embedData.previewDescription || '',
    previewWebsite: embedData.previewWebsite || '',
    ...(typeof embedData.height === 'number' ? { height: embedData.height } : {}),
    ...(embedData.width !== undefined ? { width: embedData.width } : {}),
    ...(typeof embedData.aspectRatio === 'number' ? { aspectRatio: embedData.aspectRatio } : {}),
    ...(style ? { style } : {}),
  });
}

/**
 * Transform simple button block to Builder button block
 */
function transformButtonBlock(item: { button: Record<string, any> }): any {
  const defaultStyle = {
    justifyContent: 'center',
    width: 160,
    height: 40,
    borderRadius: 8,
  };
  const buttonData = item.button || {};
  const isMultiButton = Array.isArray(buttonData.buttons);

  if (isMultiButton) {
    const align = buttonData.align;
    return createBlock('button', {
      buttons: buttonData.buttons.map((button: Record<string, any>, index: number) => ({
        id: generateLocalId('button', index),
        label: button.label,
        url: button.url || '',
        fullWidth: button.fullWidth || false,
        variant: button.variant,
        style: {
          ...(button.style
            ? {
                ...button.style,
                ...(button.style.align ? { justifyContent: button.style.align } : {}),
              }
            : {}),
          ...(button.style?.align ? {} : buildAlignmentStyle(align)),
        },
      })),
      ...(typeof buttonData.fullWidth === 'boolean' ? { fullWidth: buttonData.fullWidth } : {}),
      ...(align ? { style: { justifyContent: align } } : {}),
    });
  }

  return createBlock('button', {
    buttons: [
      {
        id: generateLocalId('button', 0),
        label: buttonData.label,
        url: buttonData.url || '',
        fullWidth: buttonData.fullWidth || false,
        variant: buttonData.variant,
        style: {
          ...defaultStyle,
          ...(buttonData.style
            ? {
                ...buttonData.style,
                ...(buttonData.style.align ? { justifyContent: buttonData.style.align } : {}),
              }
            : {}),
        },
      },
    ],
    ...(typeof buttonData.fullWidth === 'boolean' ? { fullWidth: buttonData.fullWidth } : {}),
    ...(buttonData.style?.align ? { style: { justifyContent: buttonData.style.align } } : {}),
  });
}

function transformIconBlock(item: { icon: string | Record<string, any> }): any {
  const iconData = typeof item.icon === 'string' ? { iconName: item.icon } : item.icon || {};
  const style = normalizeCommonBlockStyle(iconData.style, iconData.align);

  return createBlock('icon', {
    ...(iconData.iconName ? { iconName: iconData.iconName } : {}),
    ...(iconData.iconSvg ? { icon: iconData.iconSvg } : {}),
    ...(typeof iconData.height === 'number' ? { height: iconData.height } : {}),
    ...(typeof iconData.width === 'number' ? { width: iconData.width } : {}),
    ...(iconData.iconColor ? { iconColor: iconData.iconColor } : {}),
    ...(style ? { style } : {}),
  });
}

const CALLOUT_STYLE_COLORS = {
  note: '239, 68, 68',
  info: '59, 130, 246',
  warning: '161, 98, 7',
  caution: '249, 115, 22',
  success: '34, 197, 94',
  question: '75, 85, 99',
};

function getCalloutStyleColor(style?: string): string {
  if (style && style in CALLOUT_STYLE_COLORS) {
    return CALLOUT_STYLE_COLORS[style as keyof typeof CALLOUT_STYLE_COLORS];
  }
  return CALLOUT_STYLE_COLORS.info;
}

function hexToRgbString(hex: string): string {
  const cleanHex = hex.replace('#', '');
  const full =
    cleanHex.length === 3
      ? cleanHex
          .split('')
          .map((c) => c + c)
          .join('')
      : cleanHex;
  const r = parseInt(full.slice(0, 2), 16) || 0;
  const g = parseInt(full.slice(2, 4), 16) || 0;
  const b = parseInt(full.slice(4, 6), 16) || 0;
  return `${r}, ${g}, ${b}`;
}

function isCalloutBackgroundLight(colorRgb: string, bgOpacity: number): boolean {
  if (bgOpacity < 0.5) return true;
  const parts = colorRgb.split(',').map((p) => parseInt(p.trim(), 10));
  if (parts.length >= 3 && !parts.some(isNaN)) {
    const [r = 255, g = 255, b = 255] = parts;
    const mixedR = r * bgOpacity + 255 * (1 - bgOpacity);
    const mixedG = g * bgOpacity + 255 * (1 - bgOpacity);
    const mixedB = b * bgOpacity + 255 * (1 - bgOpacity);
    const brightness = (mixedR * 299 + mixedG * 587 + mixedB * 114) / 1000;
    return brightness > 128;
  }
  return true;
}

function sanitizeCalloutLeaves(content: any[]): any[] {
  return content.map((node) => {
    if (!node || typeof node !== 'object') return node;
    const copy = { ...node };
    if (
      typeof copy.color === 'string' &&
      ['#ffffff', '#fff', 'white', 'rgb(255, 255, 255)', 'rgba(255, 255, 255, 1)'].includes(
        copy.color.trim().toLowerCase(),
      )
    ) {
      delete copy.color;
    }
    if (Array.isArray(copy.children)) {
      copy.children = sanitizeCalloutLeaves(copy.children);
    }
    return copy;
  });
}

function transformCalloutBlock(item: { callout_box: string | Record<string, any> }): any {
  const calloutData =
    typeof item.callout_box === 'string' ? { content: item.callout_box } : item.callout_box || {};
  // An unstyled callout uses `note`, which the builder draws in the theme's primary color.
  const calloutStyle = calloutData.calloutStyle || 'note';
  const rawContent = Array.isArray(calloutData.content)
    ? richContentToSlate(calloutData.content, 'callout_box.content')
    : textToSlate(calloutData.content || '');
  const bgOpacity = typeof calloutData.bgOpacity === 'number' ? calloutData.bgOpacity : 0.15;
  const colorRgb = calloutData.customColor
    ? hexToRgbString(calloutData.customColor)
    : getCalloutStyleColor(calloutStyle);
  const isLight = isCalloutBackgroundLight(colorRgb, bgOpacity);
  const content =
    isLight && Array.isArray(rawContent) ? sanitizeCalloutLeaves(rawContent) : rawContent;

  return createBlock('callout_box', {
    content,
    calloutStyle,
    ...(typeof calloutData.hideIcon === 'boolean' ? { hideIcon: calloutData.hideIcon } : {}),
    ...(typeof calloutData.bgOpacity === 'number' ? { bgOpacity: calloutData.bgOpacity } : {}),
    ...(typeof calloutData.borderWidth === 'number'
      ? { borderWidth: calloutData.borderWidth }
      : {}),
    ...(calloutData.customColor ? { customColor: calloutData.customColor } : {}),
  });
}

function transformDividerBlock(item: { divider: true | Record<string, any> }): any {
  const dividerData = item.divider === true ? {} : item.divider || {};
  return createBlock('divider', {
    ...(dividerData.style ? { style: dividerData.style } : {}),
    ...(typeof dividerData.thickness === 'number' ? { thickness: dividerData.thickness } : {}),
    ...(dividerData.color ? { color: dividerData.color } : {}),
    ...(typeof dividerData.paddingTop === 'number' ? { paddingTop: dividerData.paddingTop } : {}),
    ...(typeof dividerData.paddingBottom === 'number'
      ? { paddingBottom: dividerData.paddingBottom }
      : {}),
    ...(dividerData.label ? { label: dividerData.label } : {}),
    ...(typeof dividerData.showLabel === 'boolean' ? { showLabel: dividerData.showLabel } : {}),
  });
}

function transformLabelBlock(item: { label: string | string[] | Record<string, any> }): any {
  const labelValue = item.label;
  const labels = Array.isArray(labelValue)
    ? labelValue
    : typeof labelValue === 'string'
      ? [labelValue]
      : Array.isArray(labelValue.labels)
        ? labelValue.labels
        : [];

  const normalizedLabels = labels.map((label, index) => {
    if (typeof label === 'string') {
      return {
        id: generateLocalId('label', index),
        text: label,
        variant: 'solid',
      };
    }

    return {
      id: generateLocalId('label', index),
      text: label.text,
      variant: label.variant || 'solid',
      ...(label.icon ? { icon: label.icon } : {}),
      ...(label.style ? { style: label.style } : {}),
    };
  });

  const align =
    typeof labelValue === 'object' && labelValue !== null && !Array.isArray(labelValue)
      ? labelValue.align
      : undefined;

  return createBlock('label', {
    labels: normalizedLabels,
    ...(typeof labelValue === 'object' &&
    labelValue !== null &&
    !Array.isArray(labelValue) &&
    typeof labelValue.fullWidth === 'boolean'
      ? { fullWidth: labelValue.fullWidth }
      : {}),
    ...(align ? { style: { justifyContent: align } } : {}),
  });
}

function transformBlockQuoteBlock(value: SimpleBlockQuoteBlock['block_quote']): any {
  const quoteData = Array.isArray(value) ? { content: value } : value || {};

  return createBlock('block_quote', {
    content: inlineTextContentToSlateElement(quoteData.content, 'block-quote').map((node) =>
      applySmartTextStyle(node, quoteData.style),
    ),
    ...(normalizeTextBlockStyle(quoteData.style)
      ? { style: normalizeTextBlockStyle(quoteData.style) }
      : {}),
  });
}

function isCodeLineArray(value: string[] | SimpleTextContent): value is string[] {
  return typeof value[0] === 'string';
}

/**
 * Normalize a code block's content to Slate text nodes before conversion.
 *
 * `codeBlockSchema` (packages/schemas/src/lite-json-schema.ts) accepts code content as a
 * string OR a string array, and the simple-builder prompt shows both forms:
 *
 *   { "code": "line one\nline two" }
 *   { "code": { "content": ["line one", "line two"] } }
 *
 * The array form must be joined into a single `{ text }` leaf. Passing the raw string[]
 * through as if it were Slate `SimpleTextContent` (an array of `{ text }` nodes) reaches
 * `inlineTextNodeToSlateChildren`, which evaluates `'text' in node` on a string primitive
 * and throws `Cannot use 'in' operator to search for 'text' in line one`. On the client
 * that throw is swallowed and the page is dropped; in the durable chat-to-proposal
 * workflow `prepareDocumentStep` is pure, so the same input crashes all 3 retries and
 * fails the whole run. Joining with `\n` renders and copies identically to the string form.
 */
function codeContentToTextContent(value: string | string[] | SimpleTextContent): SimpleTextContent {
  if (typeof value === 'string') return [{ text: value }];
  return isCodeLineArray(value) ? [{ text: value.join('\n') }] : value;
}

function transformCodeBlock(item: SimpleCodeBlock): any {
  const codeVal = item.code;
  const textBlockProps: SimpleTextBlock =
    typeof codeVal === 'string' || Array.isArray(codeVal)
      ? { p: codeContentToTextContent(codeVal) }
      : { p: codeContentToTextContent(codeVal.content), style: codeVal.style };

  const block = transformTextBlock(textBlockProps);
  if (block && block.block) {
    block.block.type = 'code';
  }
  return block;
}

/**
 * Transform simple chart block to Builder chart block
 */
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function mergePlainRecords(
  base: Record<string, unknown>,
  override: Record<string, unknown>,
): Record<string, unknown> {
  const merged = { ...base };
  for (const [key, value] of Object.entries(override)) {
    const current = merged[key];
    merged[key] =
      isPlainRecord(current) && isPlainRecord(value) ? mergePlainRecords(current, value) : value;
  }
  return merged;
}

function transformChartBlock(item: { chart: SimpleChartBlock['chart'] }): any {
  const { type, title, labels, datasets, width, height, align, config = {} } = item.chart;
  const chartConfig = mergePlainRecords(
    {
      type,
      data: {
        labels,
        datasets,
      },
      style: {
        theme: 'default',
        width: 600,
        height: 400,
      },
      options: title
        ? {
            plugins: {
              title: { display: true, text: title },
            },
          }
        : {},
    },
    config,
  );

  return {
    type: 'block',
    block: {
      type: 'chart',
      data: {
        chartConfig,
        ...(width !== undefined ? { width } : {}),
        ...(height !== undefined ? { height } : {}),
        ...(align ? { style: { justifyContent: align } } : {}),
      },
    },
  };
}

/**
 * Transform simple table block to Builder table block
 * Parses [[variable]] syntax in table cells
 */
function transformTableCellContent(content: SimpleTableCellContent, path: string): any {
  if (typeof content === 'string') {
    const hasFormatting = content.includes('[[') || content.includes('**');
    return hasFormatting ? textToSlate(content) : content;
  }

  if (Array.isArray(content)) {
    return richContentToSlate(content, path);
  }

  if (content.kind === 'table_cell_block') {
    const blockTypeMap: Record<SimpleTableEmbeddedBlock['blockType'], SlateElement['type']> = {
      image: 'table-cell-image',
      video: 'table-cell-video',
      button: 'table-cell-button',
    };

    const data =
      content.blockType === 'button' && content.data.style
        ? {
            ...content.data,
            style: {
              ...content.data.style,
              ...(content.data.style.align ? { justifyContent: content.data.style.align } : {}),
            },
          }
        : content.data;

    return [
      {
        type: blockTypeMap[content.blockType],
        data,
        children: [{ text: '' }],
      },
      {
        type: 'paragraph',
        children: [{ text: '' }],
      },
    ];
  }

  return content;
}

function transformTableCellValue(cell: SimpleTableCellValue, path: string): any {
  if (
    typeof cell === 'string' ||
    Array.isArray(cell) ||
    (cell && typeof cell === 'object' && 'kind' in cell && cell.kind === 'table_cell_block')
  ) {
    return transformTableCellContent(cell, path);
  }

  if (cell && typeof cell === 'object' && 'content' in cell) {
    const format =
      cell.format && typeof cell.format === 'object'
        ? {
            ...cell.format,
            ...(cell.format.align ? { textAlign: cell.format.align } : {}),
          }
        : undefined;

    return {
      content: transformTableCellContent(cell.content, `${path}.content`),
      ...(format ? { format } : {}),
    };
  }

  return {
    content: transformTableCellContent('', path),
  };
}

function transformTableBlock(item: { table: SimpleTableCellValue[][] | Record<string, any> }): any {
  const tableValue = Array.isArray(item.table) ? { rows: item.table } : item.table || {};
  const processedTable = (tableValue.rows || []).map(
    (row: SimpleTableCellValue[], rowIndex: number) =>
      row.map((cell, colIndex) =>
        transformTableCellValue(cell, `table.rows[${rowIndex}][${colIndex}]`),
      ),
  );
  const style = normalizeCommonBlockStyle(tableValue.style);
  const columnCount = processedTable.reduce(
    (max: number, row: unknown[]) => Math.max(max, row.length),
    0,
  );
  const hasExplicitWidths =
    columnCount > 0 &&
    Array.isArray(tableValue.column_widths) &&
    tableValue.column_widths.length === columnCount &&
    tableValue.column_widths.every(
      (width: unknown) => typeof width === 'number' && Number.isFinite(width) && width > 0,
    );
  const columnWidths = hasExplicitWidths
    ? tableValue.column_widths
    : inferTableColumnWidths(processedTable);

  return {
    type: 'block',
    block: {
      type: 'table',
      data: {
        table: processedTable,
        ...(columnWidths.length > 0 ? { columnWidths } : {}),
        ...(style ? { style } : {}),
      },
    },
  };
}

function transformPricingTableBlock(item: SimplePricingTableBlock): any {
  const { pricing_table } = item;
  const isSemantic = 'columns' in pricing_table;
  const getColumnFormat = (
    role: SimplePricingColumnRole,
    format: 'percentage' | 'currency' | null,
  ) => {
    if (role === 'item' || role === 'text' || role === 'quantity') return null;
    if (role === 'unit_price' || role === 'line_total') return 'currency' as const;
    return format === 'currency' ? 'currency' : 'percentage';
  };
  const sourceColumns = isSemantic
    ? pricing_table.columns
    : (pricing_table.rows[0] || []).map((cell, index) => {
        const columnCount = pricing_table.rows[0]?.length ?? 0;
        const role: SimplePricingColumnRole =
          index === 0
            ? 'item'
            : index === 1
              ? 'unit_price'
              : index === columnCount - 2
                ? 'quantity'
                : index === columnCount - 1
                  ? 'line_total'
                  : (pricing_table.column_types[index] ?? 'text');
        return {
          label: typeof cell === 'string' ? cell : '',
          role,
          format: pricing_table.column_formats[index] ?? null,
        };
      });
  const sourceRows = isSemantic ? pricing_table.rows : pricing_table.rows.slice(1);
  const toBuilderFormat = (format: SimpleTableCellFormat | undefined) => {
    if (!format) return undefined;
    const { align, ...rest } = format;
    return { ...rest, ...(align ? { textAlign: align } : {}) };
  };
  const columnIds = sourceColumns.map((_, index) => `pricing-column-${index + 1}`);
  const columns = sourceColumns.map((column, index) => ({
    id: columnIds[index],
    label: column.label,
    role: column.role,
    format: getColumnFormat(column.role, column.format),
    ...(typeof pricing_table.column_widths?.[index] === 'number'
      ? { width: pricing_table.column_widths[index] }
      : {}),
    ...(pricing_table.column_cell_formats?.[index]
      ? { cellFormat: toBuilderFormat(pricing_table.column_cell_formats[index]) }
      : {}),
  }));
  const rows = sourceRows.map((row, rowIndex) => ({
    id: `pricing-row-${rowIndex + 1}`,
    cells: Object.fromEntries(
      columnIds.map((columnId, columnIndex) => [
        columnId,
        typeof row[columnIndex] === 'string' ? row[columnIndex] : '',
      ]),
    ),
    ...(pricing_table.row_cell_formats?.[isSemantic ? rowIndex : rowIndex + 1]
      ? {
          format: toBuilderFormat(
            pricing_table.row_cell_formats[isSemantic ? rowIndex : rowIndex + 1],
          ),
        }
      : {}),
  }));

  return {
    type: 'block',
    block: {
      type: 'pricing_table',
      data: {
        table: {
          schemaVersion: 2,
          columns,
          rows,
          footerRows: pricing_table.footer_rows ?? [],
          ...(isSemantic && pricing_table.header_format
            ? { headerFormat: toBuilderFormat(pricing_table.header_format) }
            : !isSemantic && pricing_table.row_cell_formats?.[0]
              ? { headerFormat: toBuilderFormat(pricing_table.row_cell_formats[0]) }
              : {}),
        },
      },
    },
  };
}

/**
 * Transform simple quotation builder block to Builder quotation_builder block
 * The quotation builder is an interactive block, so we initialize it with empty data
 */
function transformQuotationBuilderBlock(item: {
  quotation_builder: true | Record<string, any>;
}): any {
  const data = typeof item.quotation_builder === 'object' ? item.quotation_builder : {};

  return {
    type: 'block',
    block: {
      type: 'quotation_builder',
      data,
    },
  };
}

/**
 * Transform simple space block to Builder space block
 * SpaceBlock component reads data.style.height
 */
function transformSpaceBlock(height: number): any {
  return {
    type: 'block',
    block: {
      type: 'space',
      data: {
        style: {
          height,
        },
      },
    },
  };
}

/**
 * Transform page break to Builder page break block
 */
function transformPageBreakBlock(): any {
  return {
    type: 'block',
    block: {
      type: 'page_break',
      data: {},
    },
  };
}

/**
 * Transform table of contents to Builder TOC block
 */
function transformTocBlock(item: { table_of_contents: true | Record<string, any> }): any {
  const tocValue = item.table_of_contents === true ? {} : item.table_of_contents || {};
  const style = normalizeCommonBlockStyle(tocValue.style);

  return {
    type: 'block',
    block: {
      type: 'table_of_contents',
      data: {
        ...(tocValue.include_heading_levels
          ? { includeHeadingLevels: tocValue.include_heading_levels }
          : {}),
        ...(typeof tocValue.show_page_numbers === 'boolean'
          ? { showPageNumbers: tocValue.show_page_numbers }
          : {}),
        ...(tocValue.page_number_alignment
          ? { pageNumberAlignment: tocValue.page_number_alignment }
          : {}),
        ...(style ? { style } : {}),
      },
    },
  };
}

// ============================================================================
// FILLABLE BLOCK TRANSFORMERS (document flow blocks)
// ============================================================================

/**
 * Transform simple signature block to Builder signature_block
 * Signature blocks are now regular blocks in document flow (not absolute positioned)
 */
/**
 * Transform Editor signature block
 * Internal type: 'signature_block'
 * Data: is_recipient_field = false
 */
function transformEditorSignatureBlock(item: SimpleEditorSignatureBlock): any {
  const data = item.editor_signature_block === true ? {} : item.editor_signature_block;
  const alignmentStyle = buildTextAlignStyle(data.align);

  return {
    type: 'block',
    block: {
      type: 'signature_block',
      data: {
        placeholder: data.placeholder || 'Click to sign',
        signature_text_placeholder: data.text_placeholder || 'Type your name',
        field_value: null,
        style: {
          width: 200,
          height: 80,
          ...(alignmentStyle || {}),
        },
        is_recipient_field: false,
      },
    },
  };
}

/**
 * Transform Recipient signature block
 * Internal type: 'signature_block'
 * Data: is_recipient_field = true
 */
function transformRecipientSignatureBlock(item: SimpleRecipientSignatureBlock): any {
  const data = item.recipient_signature_block === true ? {} : item.recipient_signature_block;
  const alignmentStyle = buildTextAlignStyle(data.align);

  return {
    type: 'block',
    block: {
      type: 'signature_block',
      data: {
        placeholder: data.placeholder || 'Click to sign',
        signature_text_placeholder: data.text_placeholder || 'Type your name',
        field_value: null,
        style: {
          width: 200,
          height: 80,
          ...(alignmentStyle || {}),
        },
        is_recipient_field: true,
      },
    },
  };
}

/**
 * Transform simple initials block to Builder initials_block
 * Initials blocks are now regular blocks in document flow
 */
/**
 * Transform Recipient initials block
 * Internal type: 'initials_block'
 * Data: is_recipient_field = true
 */
function transformRecipientInitialsBlock(item: SimpleRecipientInitialsBlock): any {
  const data = item.recipient_initials_block === true ? {} : item.recipient_initials_block;
  const alignmentStyle = buildTextAlignStyle(data.align);

  return {
    type: 'block',
    block: {
      type: 'initials_block',
      data: {
        placeholder: data.placeholder || 'Click to add initials',
        field_value: null,
        style: {
          width: 150,
          height: 60,
          ...(alignmentStyle || {}),
        },
        is_recipient_field: true,
      },
    },
  };
}

/**
 * Transform simple editor initials block to Builder initials_block
 */
function transformEditorInitialsBlock(item: SimpleEditorInitialsBlock): any {
  const data = item.editor_initials_block === true ? {} : item.editor_initials_block;
  const alignmentStyle = buildTextAlignStyle(data.align);

  return {
    type: 'block',
    block: {
      type: 'initials_block',
      data: {
        placeholder: data.placeholder || 'Click to add initials',
        field_value: null,
        style: {
          width: 150,
          height: 60,
          ...(alignmentStyle || {}),
        },
        is_recipient_field: false,
      },
    },
  };
}

/**
 * Transform simple stamp block to Builder stamp_block
 * Stamp blocks are now regular blocks in document flow
 */
/**
 * Transform Recipient stamp block
 * Internal type: 'stamp_block'
 * Data: is_recipient_field = true
 */
function transformRecipientStampBlock(item: SimpleRecipientStampBlock): any {
  const data = item.recipient_stamp_block === true ? {} : item.recipient_stamp_block;
  const alignmentStyle = buildTextAlignStyle(data.align);

  return {
    type: 'block',
    block: {
      type: 'stamp_block',
      data: {
        placeholder: data.placeholder || 'Click to add stamp',
        field_value: null,
        style: {
          width: 150,
          height: 60,
          ...(alignmentStyle || {}),
        },
        is_recipient_field: true,
      },
    },
  };
}

/**
 * Transform simple editor stamp block to Builder stamp_block
 */
function transformEditorStampBlock(item: SimpleEditorStampBlock): any {
  const data = item.editor_stamp_block === true ? {} : item.editor_stamp_block;
  const alignmentStyle = buildTextAlignStyle(data.align);

  return {
    type: 'block',
    block: {
      type: 'stamp_block',
      data: {
        placeholder: data.placeholder || 'Click to add stamp',
        field_value: null,
        style: {
          width: 150,
          height: 60,
          ...(alignmentStyle || {}),
        },
        is_recipient_field: false,
      },
    },
  };
}

/**
 * Generate a unique field ID using a combination of timestamp and random string.
 * This ensures globally unique IDs across all fillable text blocks and paragraphs,
 * preventing multiple fields from sharing the same Jotai atom in view mode.
 */
function generateUniqueFieldId(): string {
  // Use a combination of timestamp (base36) and random string for uniqueness
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 10);
  return `field_${timestamp}_${randomPart}`;
}

function isSimpleFillableChildItem(value: unknown): value is SimpleFillableChildItem {
  return (
    !!value &&
    typeof value === 'object' &&
    ('block_quote' in value ||
      'h1' in value ||
      'h2' in value ||
      'h3' in value ||
      'h4' in value ||
      'h5' in value ||
      'p' in value ||
      'bullets' in value ||
      'numbered' in value)
  );
}

function fillableChildItemToSlateContent(item: SimpleFillableChildItem): SlateElement[] {
  if ('block_quote' in item) {
    if (Array.isArray(item.block_quote)) {
      return [
        {
          type: 'block-quote',
          children: fillableTextContentToSlateChildren(item.block_quote),
        },
      ];
    }

    return [
      applySmartTextStyle(
        {
          type: 'block-quote',
          children: fillableTextContentToSlateChildren(item.block_quote.content),
        },
        item.block_quote.style,
      ),
    ];
  }

  const heading = getHeading(item);

  if (heading) {
    return fillableTextContentToSlateElement(heading.content, heading.type).map((node) =>
      applySmartTextStyle(node, item.style),
    );
  }

  if (item.p) {
    return fillableTextContentToSlateElement(item.p).map((node) =>
      applySmartTextStyle(node, item.style),
    );
  }

  if (item.bullets) {
    return fillableStructuredListToSlate(item.bullets, 'bulleted-list', item.style?.listStyle).map(
      (node) => applySmartTextStyle(node, item.style),
    );
  }

  if (item.numbered) {
    return fillableStructuredListToSlate(item.numbered, 'numbered-list', item.style?.listStyle).map(
      (node) => applySmartTextStyle(node, item.style),
    );
  }

  return [{ type: 'paragraph', children: [{ text: '' }] }];
}

/**
 * Transform simple fillable text block to Builder fillable_text block
 */
function transformFillableTextBlock(item: SimpleFillableTextBlock): any {
  const fillableValue = item.fillable_text;
  const content = Array.isArray(fillableValue)
    ? fillableTextContentToSlateElement(fillableValue)
    : Array.isArray(fillableValue?.content)
      ? fillableValue.content
          .filter((child): child is SimpleFillableChildItem => isSimpleFillableChildItem(child))
          .flatMap((child) => fillableChildItemToSlateContent(child))
      : [{ type: 'paragraph', children: [{ text: '' }] }];
  const style =
    !Array.isArray(fillableValue) && fillableValue?.style
      ? normalizeTextBlockStyle(fillableValue.style)
      : undefined;

  return {
    type: 'block',
    block: {
      type: 'fillable_text',
      data: {
        content: content.length > 0 ? content : [{ type: 'paragraph', children: [{ text: '' }] }],
        is_recipient_field: true,
        ...(style ? { style } : {}),
      },
    },
  };
}

/**
 * Transform simple editor fillable text block to Builder fillable_text block
 */
function transformEditorFillableTextBlock(item: SimpleEditorFillableTextBlock): any {
  const fillableValue = item.editor_fillable_text;
  const content = Array.isArray(fillableValue)
    ? fillableTextContentToSlateElement(fillableValue)
    : Array.isArray(fillableValue?.content)
      ? fillableValue.content
          .filter((child): child is SimpleFillableChildItem => isSimpleFillableChildItem(child))
          .flatMap((child) => fillableChildItemToSlateContent(child))
      : [{ type: 'paragraph', children: [{ text: '' }] }];
  const style =
    !Array.isArray(fillableValue) && fillableValue?.style
      ? normalizeTextBlockStyle(fillableValue.style)
      : undefined;

  return {
    type: 'block',
    block: {
      type: 'fillable_text',
      data: {
        content: content.length > 0 ? content : [{ type: 'paragraph', children: [{ text: '' }] }],
        is_recipient_field: false,
        ...(style ? { style } : {}),
      },
    },
  };
}

// ============================================================================
// SMART BLOCK TRANSFORMERS
// ============================================================================

/**
 * Transform smart block items with IDs
 * Some smart blocks use Slate format for title/description, others use plain strings
 */
function transformSmartBlockItems(items: SimpleSmartBlockItem[]): any[] {
  return items.map((item, index) => ({
    id: index + 1,
    title: smartTextContentToSlateValue(item.title),
    description: smartTextContentToSlateValue(item.description),
    icon: item.icon,
    ...(item.color ? { color: resolveThemeColorExpression(item.color) } : {}),
    ...(item.bgColor ? { bgColor: resolveThemeColorExpression(item.bgColor) } : {}),
  }));
}

function buildSmartBlockData(
  items: SimpleSmartBlockItem[],
  config: Record<string, any>,
): Record<string, any> {
  return {
    items: transformSmartBlockItems(items),
    ...(config.layoutId ? { layoutId: config.layoutId } : {}),
    ...(config.style ? { style: config.style } : {}),
  };
}

/**
 * Transform timeline block
 * Supports array format or object with items and viewMode
 */
function transformTimelineBlock(timeline: SimpleSmartBlockItem[] | Record<string, any>): any {
  const { items, config } = normalizeSmartBlockValue<Record<string, any>>(timeline);
  return createSmartBlock('timeline', {
    ...buildSmartBlockData(items, config),
    viewMode: config.viewMode || SMART_BLOCK_DEFAULTS.timeline.viewMode,
  });
}

/**
 * Transform process flow block
 * Supports array format or object with items and columns
 */
function transformProcessFlowBlock(processFlow: SimpleSmartBlockItem[] | Record<string, any>): any {
  const { items, config } = normalizeSmartBlockValue<Record<string, any>>(processFlow);
  return createSmartBlock('process_flow', {
    ...buildSmartBlockData(items, config),
    ...(config.columns ? { columns: config.columns } : {}),
  });
}

/**
 * Transform steps block (uses Slate format)
 */
function transformStepsBlock(steps: SimpleSmartBlockItem[] | Record<string, any>): any {
  const { items, config } = normalizeSmartBlockValue<Record<string, any>>(steps);
  return createSmartBlock('steps', buildSmartBlockData(items, config));
}

/**
 * Transform arrow block (uses Slate format)
 * Supports array format or object with items and direction
 */
function transformArrowBlock(arrow: SimpleSmartBlockItem[] | Record<string, any>): any {
  const { items, config } = normalizeSmartBlockValue<Record<string, any>>(arrow);
  return createSmartBlock('arrow', {
    ...buildSmartBlockData(items, config),
    ...(config.direction === 'bottom' ? { direction: 'bottom' } : {}),
  });
}

/**
 * Transform staircase block
 */
function transformStaircaseBlock(staircase: SimpleSmartBlockItem[] | Record<string, any>): any {
  const { items, config } = normalizeSmartBlockValue<Record<string, any>>(staircase);
  return createSmartBlock('staircase', buildSmartBlockData(items, config));
}

/**
 * Transform pyramid block
 * Supports array format or object with items and orientation
 */
function transformPyramidBlock(
  pyramid: SimpleSmartBlockItem[] | Record<string, any>,
  type: 'pyramid' | 'funnel' = 'pyramid',
): any {
  const { items, config } = normalizeSmartBlockValue<Record<string, any>>(pyramid);
  const defaultOrientation =
    type === 'funnel'
      ? SMART_BLOCK_DEFAULTS.funnel.orientation
      : SMART_BLOCK_DEFAULTS.pyramid.orientation;

  return createSmartBlock(type, {
    ...buildSmartBlockData(items, config),
    orientation: config.orientation || defaultOrientation,
    ...(typeof config.textInside === 'boolean' ? { textInside: config.textInside } : {}),
  });
}

/**
 * Transform circular arrow block
 */
function transformCircularArrowBlock(
  circularArrow: SimpleSmartBlockItem[] | Record<string, any>,
): any {
  const { items, config } = normalizeSmartBlockValue<Record<string, any>>(circularArrow);
  return createSmartBlock('circular_arrow', buildSmartBlockData(items, config));
}

function transformColumnSmartBlock(
  type: 'icon_text' | 'top_circle_boxes' | 'solid_boxes' | 'side_line_boxes' | 'quote_boxes',
  value: SimpleSmartBlockItem[] | Record<string, any>,
): any {
  const { items, config } = normalizeSmartBlockValue<Record<string, any>>(value);
  return createSmartBlock(type, {
    ...buildSmartBlockData(items, config),
    ...(config.columns ? { columns: config.columns } : {}),
    ...(config.align ? { textAlign: config.align } : {}),
    ...(config.orientation ? { orientation: config.orientation } : {}),
  });
}

// ============================================================================
// CONTENT BLOCK TRANSFORMER
// ============================================================================

/**
 * Transform content block
 * @param type - content block type (services, team, etc.)
 * @param options - optional layout and items configuration
 */
function transformContentBlock(type: string, options?: { layout?: string; items?: string[] }): any {
  const data: any = {};

  // Add layout if provided
  if (options?.layout) {
    data.layout = options.layout;
  }

  // Add items if provided (items are UUIDs resolved from knowledge base)
  if (options?.items && options.items.length > 0) {
    data.items = options.items;
  }

  return {
    type: 'content_block',
    block: {
      type,
      data,
    },
  };
}

// ============================================================================
// LAYOUT TRANSFORMER
// ============================================================================

/**
 * Transform a multi-column row
 */
function transformRowBlock(columns: SimpleItem[][], widths?: number[]): any {
  const columnCount = columns.length;
  const defaultWidths = Array(columnCount).fill(Math.floor(100 / columnCount));

  return {
    type: 'row',
    data: {
      widths: widths || defaultWidths,
    },
    items: columns.map((columnItems) => ({
      type: 'column',
      items: columnItems.map(transformItem),
    })),
  };
}

// ============================================================================
// MAIN ITEM TRANSFORMER
// ============================================================================

/**
 * Transform a single simple item to Builder format
 */
function transformItem(item: SimpleItem): any {
  // Text blocks
  if (isSimpleTextBlock(item)) {
    return transformTextBlock(item);
  }

  if (isSimpleTextGroupBlock(item)) {
    return transformTextGroupBlock(item);
  }

  // Media blocks
  if (isSimpleImageBlock(item)) {
    return transformImageBlock(item);
  }

  if (isSimpleVideoBlock(item)) {
    return transformVideoBlock(item);
  }

  if (isSimpleEmbedWebpageBlock(item)) {
    return transformEmbedWebpageBlock(item);
  }

  if (isSimpleButtonBlock(item)) {
    return transformButtonBlock(item);
  }

  if (isSimpleIconBlock(item)) {
    return transformIconBlock(item);
  }

  if (isSimpleCalloutBlock(item)) {
    return transformCalloutBlock(item);
  }

  if (isSimpleDividerBlock(item)) {
    return transformDividerBlock(item);
  }

  if (isSimpleLabelBlock(item)) {
    return transformLabelBlock(item);
  }

  if (isSimpleBlockQuoteBlock(item)) {
    return transformBlockQuoteBlock(item.block_quote);
  }

  if (isSimpleCodeBlock(item)) {
    return transformCodeBlock(item);
  }

  // Data blocks
  if (isSimpleChartBlock(item)) {
    return transformChartBlock(item);
  }

  if (isSimpleTableBlock(item)) {
    return transformTableBlock(item);
  }

  if (isSimplePricingTableBlock(item)) {
    return transformPricingTableBlock(item);
  }

  if (isSimpleQuotationBuilderBlock(item)) {
    return transformQuotationBuilderBlock(item);
  }

  // Smart blocks
  if (isSimpleTimelineBlock(item)) {
    return transformTimelineBlock(item.timeline);
  }

  if (isSimpleProcessFlowBlock(item)) {
    return transformProcessFlowBlock(item.process_flow);
  }

  if (isSimpleStepsBlock(item)) {
    return transformStepsBlock(item.steps);
  }

  if (isSimpleArrowBlock(item)) {
    return transformArrowBlock(item.arrow);
  }

  if (isSimpleStaircaseBlock(item)) {
    return transformStaircaseBlock(item.staircase);
  }

  if (isSimplePyramidBlock(item)) {
    return transformPyramidBlock(item.pyramid);
  }

  if (isSimpleFunnelBlock(item)) {
    return transformPyramidBlock(item.funnel, 'funnel');
  }

  if (isSimpleCircularArrowBlock(item)) {
    return transformCircularArrowBlock(item.circular_arrow);
  }

  if (isSimpleIconTextBlock(item)) {
    return transformColumnSmartBlock('icon_text', item.icon_text);
  }

  if (isSimpleTopCircleBoxesBlock(item)) {
    return transformColumnSmartBlock('top_circle_boxes', item.top_circle_boxes);
  }

  if (isSimpleSolidBoxesBlock(item)) {
    return transformColumnSmartBlock('solid_boxes', item.solid_boxes);
  }

  if (isSimpleSideLineBoxesBlock(item)) {
    return transformColumnSmartBlock('side_line_boxes', item.side_line_boxes);
  }

  if (isSimpleQuoteBoxesBlock(item)) {
    return transformColumnSmartBlock('quote_boxes', item.quote_boxes);
  }

  // Content blocks
  if (isSimpleContentBlock(item)) {
    const type = getContentBlockType(item);
    if (type) {
      // Get the value - could be true or object with layout
      const value = (item as any)[type];
      const options =
        typeof value === 'object' && value !== null && !Array.isArray(value) ? value : undefined;
      return transformContentBlock(type, options);
    }
  }

  // Layout
  if (isSimpleRowBlock(item)) {
    return transformRowBlock(item.columns, item.widths);
  }

  // Utility blocks
  if (isSimpleSpaceBlock(item)) {
    return transformSpaceBlock(item.space);
  }

  if (isSimplePageBreakBlock(item)) {
    return transformPageBreakBlock();
  }

  if (isSimpleTocBlock(item)) {
    return transformTocBlock(item);
  }

  // Fillable blocks (document flow)
  // Fillable blocks (document flow)
  if (isSimpleEditorSignatureBlock(item)) {
    return transformEditorSignatureBlock(item);
  }

  if (isSimpleRecipientSignatureBlock(item)) {
    return transformRecipientSignatureBlock(item);
  }

  if (isSimpleRecipientInitialsBlock(item)) {
    return transformRecipientInitialsBlock(item);
  }

  if (isSimpleEditorInitialsBlock(item)) {
    return transformEditorInitialsBlock(item);
  }

  if (isSimpleRecipientStampBlock(item)) {
    return transformRecipientStampBlock(item);
  }

  if (isSimpleEditorStampBlock(item)) {
    return transformEditorStampBlock(item);
  }

  if (isSimpleFillableTextBlock(item)) {
    return transformFillableTextBlock(item);
  }

  if (isSimpleEditorFillableTextBlock(item)) {
    return transformEditorFillableTextBlock(item);
  }

  if (
    item &&
    typeof item === 'object' &&
    'protected_fillable_block' in (item as Record<string, unknown>)
  ) {
    return transformSpaceBlock(0);
  }

  // Fallback - return empty text block
  console.warn('Unknown simple item type:', item);
  return {
    type: 'block',
    block: {
      type: 'text',
      data: {
        content: [{ type: 'paragraph', children: [{ text: '' }] }],
      },
    },
  };
}

// ============================================================================
// PAGE TRANSFORMER
// ============================================================================

/**
 * Generate position string for page ordering
 */
function generatePosition(index: number): string {
  // Use letters a-z for positions
  const base = 'abcdefghijklmnopqrstuvwxyz';
  if (index < 26) return base[index] ?? 'a';
  // For more than 26 pages, use combinations
  const first = Math.floor(index / 26) - 1;
  const second = index % 26;
  const firstChar = first >= 0 && first < 26 ? base[first] : '';
  const secondChar = base[second] ?? 'a';
  return firstChar + secondChar;
}

function mapPreservedHeaderFooter(
  headerFooter: SimplePageHeaderFooterPreserve | undefined,
): Record<string, unknown> | undefined {
  if (!headerFooter) {
    return undefined;
  }

  const nextConfig: Record<string, unknown> = {};

  if (typeof headerFooter.hideForThisSection === 'boolean') {
    nextConfig.hideForThisSection = headerFooter.hideForThisSection;
  }

  if (headerFooter.override?.elements?.length) {
    nextConfig.override = {
      elements: headerFooter.override.elements.map((element: SimplePageHeaderFooterElement) => ({
        ...(typeof element.id === 'string' ? { id: element.id } : {}),
        slot: element.slot,
        type: element.type,
        ...(typeof element.value === 'string' ? { value: element.value } : {}),
        ...(typeof element.imageUrl === 'string' ? { imageUrl: element.imageUrl } : {}),
        ...(typeof element.enabled === 'boolean' ? { enabled: element.enabled } : {}),
        ...(typeof element.width === 'number' ? { width: element.width } : {}),
        ...(typeof element.height === 'number' ? { height: element.height } : {}),
      })),
    };
  }

  return Object.keys(nextConfig).length > 0 ? nextConfig : undefined;
}

/**
 * Transform a simple page to Builder page format
 */
function transformPage(page: SimplePage, index: number): any {
  const builderPage: any = {
    type: 'page',
    name: page.name,
    position: generatePosition(index),
    root_node: {
      type: 'column',
      items: page.content.map(transformItem),
    },
  };
  const pageData: Record<string, unknown> = {};
  const cardBackground: Record<string, unknown> = {};
  const appearance = page.appearance;

  pageData.visibility = typeof appearance?.visibility === 'boolean' ? appearance.visibility : true;

  // Per-page landscape override. `false` must be written, not dropped: it is how the override is
  // turned off, and the page then follows the document's own orientation. The builder's own
  // toggle writes the same. Omitting the key would leave a previous override in place, because
  // the client merges the converted page over the current page data.
  if (typeof page.isLandscape === 'boolean') {
    pageData.isLandscape = page.isLandscape;
  }

  if (appearance?.card) {
    if (appearance.card.mode === 'theme') {
      cardBackground.type = 'solid';
      cardBackground.useThemeCardColor = true;
      if (typeof appearance.card.value === 'string' && appearance.card.value.trim().length > 0) {
        cardBackground.colorCode = resolveThemeColorExpression(appearance.card.value);
      }
    } else {
      cardBackground.type = appearance.card.mode;
      cardBackground.useThemeCardColor = false;
      if (typeof appearance.card.value === 'string' && appearance.card.value.trim().length > 0) {
        cardBackground.colorCode = resolveThemeColorExpression(appearance.card.value);
      }
    }
  }

  if (appearance?.layout) {
    pageData.pageLayout = appearance.layout.mode;

    const layoutSize: Record<string, unknown> = {};
    if (appearance.layout.mode === 'top' && typeof appearance.layout.size?.height === 'number') {
      layoutSize.top = { height: appearance.layout.size.height };
    }
    if (
      (appearance.layout.mode === 'left' || appearance.layout.mode === 'right') &&
      typeof appearance.layout.size?.width === 'number'
    ) {
      layoutSize.side = { width: appearance.layout.size.width };
    }
    if (Object.keys(layoutSize).length > 0) {
      pageData.layoutSize = layoutSize;
    }

    if (appearance.layout.media?.image) {
      if (typeof appearance.layout.media.image === 'string') {
        cardBackground.image_prompt = appearance.layout.media.image;
      } else {
        // Server-side generation writes extra keys into the lite JSON (FEAT-042);
        // the raw record is preserved by normalizePageLayout, so widen the type.
        const layoutImage = appearance.layout.media.image;
        const resolvedUrl =
          layoutImage.url ?? (layoutImage.ref ? imageRefUrlMap?.[layoutImage.ref] : undefined);
        if (resolvedUrl) {
          cardBackground.imageUrl = resolvedUrl;
        }
        if (layoutImage.prompt) {
          cardBackground.image_prompt = layoutImage.prompt;
        }
        // Server-side generation failure state (FEAT-042); the layout effect
        // treats `image_generation_status: 'failed'` as do-not-generate.
        if (typeof layoutImage.image_generation_error === 'string') {
          cardBackground.image_generation_error = layoutImage.image_generation_error;
        }
        if (typeof layoutImage.image_generation_status === 'string') {
          cardBackground.image_generation_status = layoutImage.image_generation_status;
        }
        if (typeof layoutImage.image_generation_request_id === 'string') {
          cardBackground.image_generation_request_id = layoutImage.image_generation_request_id;
        }
      }
    }

    if (appearance.layout.media?.fill) {
      cardBackground.accentType = appearance.layout.media.fill.mode;
      cardBackground.accentColor = resolveThemeColorExpression(appearance.layout.media.fill.value);
    }

    if (appearance.layout.media?.type) {
      cardBackground.mediaType = appearance.layout.media.type;
    }

    if (appearance.layout.media?.fit) {
      cardBackground.fitMode = appearance.layout.media.fit;
    }

    if (
      typeof appearance.layout.media?.focus?.x === 'number' &&
      typeof appearance.layout.media?.focus?.y === 'number'
    ) {
      cardBackground.focusPoint = {
        x: appearance.layout.media.focus.x,
        y: appearance.layout.media.focus.y,
      };
    }

    if (appearance.layout.mode === 'background' && appearance.layout.overlay) {
      cardBackground.overlayEffect = appearance.layout.overlay.effect;
      if (typeof appearance.layout.overlay.intensity === 'number') {
        cardBackground.overlayIntensity = appearance.layout.overlay.intensity;
      }
      if (typeof appearance.layout.overlay.blur === 'number') {
        cardBackground.overlayBlurIntensity = appearance.layout.overlay.blur;
      }
      if (
        typeof appearance.layout.overlay.color === 'string' &&
        appearance.layout.overlay.color.trim().length > 0
      ) {
        cardBackground.overlayColor = resolveThemeColorExpression(appearance.layout.overlay.color);
      }
    }
  }

  const preservedHeaderFooter = mapPreservedHeaderFooter(page._preserve?.header_footer);
  if (preservedHeaderFooter) {
    pageData.headerFooter = preservedHeaderFooter;
  }

  if (Object.keys(cardBackground).length > 0) {
    pageData.cardBackground = cardBackground;
  }

  if (Object.keys(pageData).length > 0) {
    builderPage.data = pageData;
  }

  return builderPage;
}

// ============================================================================
// MAIN EXPORT FUNCTIONS
// ============================================================================

/**
 * Transform a complete simple document to Builder format
 *
 * @param simpleDoc - The simple JSON document
 * @returns Builder JSON format ready for the React builder
 */
export function simpleToBuilderJson(
  simpleDoc: SimpleDocument,
  imageRefUrls?: Record<string, string>,
): any[] {
  imageRefUrlMap = imageRefUrls ?? null;
  try {
    return simpleDoc.map((page, index) => transformPage(page, index));
  } finally {
    imageRefUrlMap = null;
  }
}

if (typeof window !== 'undefined') {
  (window as any).simpleToBuilderJson = simpleToBuilderJson;
}

/**
 * Transform a single simple page to Builder format
 * Useful for streaming scenarios where pages come one at a time
 *
 * @param simplePage - A single simple page
 * @param index - The page index (for position generation)
 * @returns Builder JSON page
 */
export function simplePageToBuilder(simplePage: SimplePage, index: number = 0): any {
  return transformPage(simplePage, index);
}

/**
 * Check if a JSON object looks like Simple Builder format
 * Used for backwards compatibility to detect which format is incoming
 *
 * @param json - The JSON to check
 * @returns true if it appears to be Simple Builder format
 */
export function isSimpleBuilderFormat(json: any): boolean {
  if (!Array.isArray(json) || json.length === 0) return false;

  const firstItem = json[0];

  // Simple format has 'name' and 'content' at root
  // Builder format has 'type: page' and 'root_node'
  return (
    typeof firstItem === 'object' &&
    'name' in firstItem &&
    'content' in firstItem &&
    Array.isArray(firstItem.content) &&
    !('type' in firstItem && firstItem.type === 'page')
  );
}

/**
 * Transform JSON to Builder format, auto-detecting the input format
 * Provides backwards compatibility - passes through Builder format unchanged
 *
 * @param json - Either Simple or Builder format JSON
 * @returns Builder JSON format
 */
export function toBuilderJson(json: any[], imageRefUrls?: Record<string, string>): any[] {
  if (isSimpleBuilderFormat(json)) {
    return simpleToBuilderJson(json as SimpleDocument, imageRefUrls);
  }
  // Already in Builder format, pass through
  return json;
}

/**
 * Transform the AI-generation-friendly simple format to Builder JSON.
 * Accepts much simpler shorthand such as { h1: "Title" }, string lists,
 * simple smart block items, and legacy page layout fields.
 */
export function liteJsonToBuilderJson(
  json: unknown,
  imageRefUrls?: Record<string, string>,
  /** Theme canvas color; a page `layout.color` equal to it is dropped. */
  canvasColor?: string,
): any[] {
  return simpleToBuilderJson(normalizeLiteJsonDocument(json, canvasColor), imageRefUrls);
}

// ============================================================================
// VARIABLE EXTRACTION UTILITIES
// ============================================================================

/**
 * Recursively extract all custom variable keys from any object/array structure
 * Looks for mention elements with variable.type === 'custom' or variable.key starting with 'custom.'
 */
function extractVariableKeysFromNode(node: any, keys: Set<string>): void {
  if (!node || typeof node !== 'object') return;

  // Check if this is a mention element with a custom variable
  if (node.type === 'mention' && node.variable?.key) {
    const key = node.variable.key;
    // Include if it's explicitly marked as custom OR starts with 'custom.'
    if (node.variable.type === 'custom' || key.startsWith('custom.')) {
      keys.add(key);
    }
  }

  // Recursively process arrays
  if (Array.isArray(node)) {
    for (const item of node) {
      extractVariableKeysFromNode(item, keys);
    }
    return;
  }

  // Recursively process object properties
  for (const value of Object.values(node)) {
    extractVariableKeysFromNode(value, keys);
  }
}

/**
 * Extract all unique custom variable keys from Builder JSON pages
 * Returns an array of variable keys that need to be created in the backend
 *
 * @param pages - Array of Builder JSON pages
 * @returns Array of unique custom variable keys (e.g., ['custom.clientName', 'custom.projectDeadline'])
 */
export function extractCustomVariableKeys(pages: any[]): string[] {
  const keys = new Set<string>();

  for (const page of pages) {
    extractVariableKeysFromNode(page, keys);
  }

  return Array.from(keys).sort();
}

/**
 * Extract all unique variable keys (both system and custom) from Builder JSON pages
 *
 * @param pages - Array of Builder JSON pages
 * @returns Object with 'system' and 'custom' arrays of variable keys
 */
export function extractAllVariableKeys(pages: any[]): {
  system: string[];
  custom: string[];
} {
  const systemKeys = new Set<string>();
  const customKeys = new Set<string>();

  function processNode(node: any): void {
    if (!node || typeof node !== 'object') return;

    // Check if this is a mention element
    if (node.type === 'mention' && node.variable?.key) {
      const key = node.variable.key;
      if (node.variable.type === 'system' || SYSTEM_VARIABLE_KEYS.includes(key)) {
        systemKeys.add(key);
      } else {
        customKeys.add(key);
      }
    }

    // Recursively process
    if (Array.isArray(node)) {
      for (const item of node) {
        processNode(item);
      }
      return;
    }

    for (const value of Object.values(node)) {
      processNode(value);
    }
  }

  for (const page of pages) {
    processNode(page);
  }

  return {
    system: Array.from(systemKeys).sort(),
    custom: Array.from(customKeys).sort(),
  };
}
